import {
  ContextMenuContext,
  ContextMenuItem,
  ContextMenuItemKey,
  ContextMenuItemStyle,
  NodeType,
  TreeNode,
} from '../types/context-menu';
import type { GridItem } from './grid-service';
import { useSnackbarStore } from '@certara/common-react-components';

export class ContextMenuService {
  private orderLookup: Map<ContextMenuItemKey, number> = new Map([
    // Common items
    [ContextMenuItemKey.copyLink, 1],
    [ContextMenuItemKey.details, 2],

    // Folder items
    [ContextMenuItemKey.editFolder, 25],
    [ContextMenuItemKey.deleteFolder, 26],

    // File items
    [ContextMenuItemKey.downloadFile, 38],
  ]);

  /**
   * Build context menu items based on the provided context
   */
  async buildContextMenu(
    context: ContextMenuContext | null,
    selectedRegion: string = 'US'
  ): Promise<ContextMenuItem[]> {
    const result: ContextMenuItem[] = [];

    if (!context || !context.selectedNode) {
      return result;
    }

    // Check if this is a TreeNode (has NodeType) or GridItem (has IsFolder)
    const isTreeNode =
      'NodeType' in context.selectedNode && context.selectedNode.NodeType != null;

    // Add common items
    result.push(...this.buildCommonItems());

    // Add node-specific items based on node type
    if (isTreeNode) {
      // TreeNode case
      const treeNode = context.selectedNode as TreeNode;
      switch (treeNode.NodeType) {
        case NodeType.FileUseHistory:
        case NodeType.FileUseSet:
          result.push(...(await this.buildFileItems(context, selectedRegion)));
          break;
        case NodeType.Sub:
          result.push(...this.buildSubItems(context));
          break;
        case NodeType.Section:
        case NodeType.RepeatSection:
        case NodeType.SectionExtension:
          result.push(...(await this.buildSectionItems(context)));
          break;
      }
    } else if ('IsFolder' in context.selectedNode) {
      // GridItem case - handle folder/file context menus
      const gridItem = context.selectedNode as GridItem;
      if (gridItem.IsFolder) {
        result.push(...this.buildFolderItems(context));
      } else {
        result.push(...(await this.buildFileItems(context, selectedRegion)));
      }
    }

    // Sort by order and return
    return result.sort((a, b) => (a.order > b.order ? 1 : -1));
  }

  /**
   * Execute a context menu item action
   */
  async executeAction(
    key: ContextMenuItemKey,
    context: ContextMenuContext,
    subItemId?: ContextMenuItemKey
  ): Promise<void> {
    // subItemId reserved for future submenu implementations
    void subItemId;
    switch (key) {
      case ContextMenuItemKey.copyLink:
        await this.copyLink(context);
        break;
      case ContextMenuItemKey.details:
        await this.showDetails(context);
        break;
      case ContextMenuItemKey.downloadFile:
        await this.downloadFile(context);
        break;
      case ContextMenuItemKey.editFolder:
        await this.editFolder(context);
        break;
      case ContextMenuItemKey.deleteFolder:
        await this.deleteFolder(context);
        break;
      default:
        // Action handler not implemented
        break;
    }
  }

  private buildCommonItems(): ContextMenuItem[] {
    const result: ContextMenuItem[] = [];

    result.push({
      key: ContextMenuItemKey.copyLink,
      displayText: 'Copy link',
      order: this.getOrderFor(ContextMenuItemKey.copyLink),
      style: ContextMenuItemStyle.default,
      disabled: false,
      items: [],
      isDropdown: false,
      icon: 'link',
    });

    result.push({
      key: ContextMenuItemKey.details,
      displayText: 'View details',
      order: this.getOrderFor(ContextMenuItemKey.details),
      style: ContextMenuItemStyle.default,
      disabled: false,
      items: [],
      isDropdown: false,
      icon: 'info',
      divider: true,
    });

    return result;
  }

  private async buildFileItems(
    context: ContextMenuContext,
    selectedRegion: string
  ): Promise<ContextMenuItem[]> {
    // selectedRegion reserved for future region-specific menu items
    void selectedRegion;
    const result: ContextMenuItem[] = [];
    const selectedNode = context.selectedNode;

    // Common file items
    const doesExist =
      selectedNode && 'DoesExist' in selectedNode ? selectedNode.DoesExist : true; // GridItems are assumed to exist
    if (doesExist) {
      result.push({
        key: ContextMenuItemKey.downloadFile,
        displayText: 'Download',
        order: this.getOrderFor(ContextMenuItemKey.downloadFile),
        style: ContextMenuItemStyle.default,
        disabled: false,
        items: [],
        isDropdown: false,
        icon: 'download',
      });
    }

    return result;
  }

  private buildFolderItems(context: ContextMenuContext): ContextMenuItem[] {
    const result: ContextMenuItem[] = [];
    const selectedNode = context.selectedNode as GridItem;

    if (selectedNode?.IsFolder) {
      result.push({
        key: ContextMenuItemKey.editFolder,
        displayText: 'Edit Folder',
        order: this.getOrderFor(ContextMenuItemKey.editFolder),
        style: ContextMenuItemStyle.default,
        disabled: false,
        items: [],
        isDropdown: false,
      });

      result.push({
        key: ContextMenuItemKey.deleteFolder,
        displayText: 'Delete Folder',
        order: this.getOrderFor(ContextMenuItemKey.deleteFolder),
        style: ContextMenuItemStyle.danger,
        disabled: false,
        items: [],
        isDropdown: false,
      });
    }

    return result;
  }

  private buildSubItems(_context: ContextMenuContext): ContextMenuItem[] {
    // Submission unit operations are not available in the Load app
    void _context;
    return [];
  }

  private async buildSectionItems(
    _context: ContextMenuContext
  ): Promise<ContextMenuItem[]> {
    // Section operations with add/edit/delete are not available in the Load app
    void _context;
    return [];
  }

  private getOrderFor(key: ContextMenuItemKey): number {
    return this.orderLookup.get(key) ?? 999;
  }

  private notifyInfo(message: string): void {
    useSnackbarStore.getState().addSnackbar(message, 'info');
  }

  // Action handlers - context parameters reserved for future implementations
  private async copyLink(context: ContextMenuContext): Promise<void> {
    void context;
    this.notifyInfo('Copy link action is not yet available.');
  }

  private async showDetails(context: ContextMenuContext): Promise<void> {
    void context;
    this.notifyInfo('Details view is not yet available.');
  }

  private async downloadFile(context: ContextMenuContext): Promise<void> {
    void context;
    this.notifyInfo('Download action is not yet available.');
  }

  private async editFolder(context: ContextMenuContext): Promise<void> {
    void context;
    this.notifyInfo('Edit folder is not yet available.');
  }

  private async deleteFolder(context: ContextMenuContext): Promise<void> {
    void context;
    this.notifyInfo('Delete folder is not yet available.');
  }
}

export const contextMenuService = new ContextMenuService();
